// Who hears about a brand's posts — shown while one brand is selected, next
// to the account list it publishes to (ADR-0029: a brand is filing, never
// authority, and this panel does not change that — it is an opt-in mailing
// list, not a grant of any kind).
//
// Two questions, one panel: "who belongs to this brand" (the roster an
// organiser curates) and "who will actually get notified" (that roster's
// opted-in members, plus the workspace's owners/admins, who hear about every
// brand regardless of membership here). Splitting them into two lists would
// leave the second question unanswered by anything on screen.

import { useCallback, useEffect, useState } from "react";
import { Loading, Spinner } from "./Spinner";
import {
  addBrandMember, brandNotificationRoster, removeBrandMember, setBrandNotifications,
  type BrandNotificationRoster,
} from "../lib/brands";
import { supabase } from "../lib/supabase";
import { listPeople, type Person } from "../lib/people";

export function BrandNotificationsCard({
  workspaceId, brandId, brandName, mayOrganise,
}: {
  workspaceId: string;
  brandId: string;
  brandName: string;
  mayOrganise: boolean;
}) {
  const [roster, setRoster] = useState<BrandNotificationRoster | null>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [people, setPeople] = useState<Person[] | null>(null);
  const [pick, setPick] = useState("");
  const busy = busyKey !== null;

  const load = useCallback(async () => {
    setRoster(await brandNotificationRoster(brandId));
  }, [brandId]);

  useEffect(() => {
    setRoster(null);
    setError(null);
    setPicking(false);
    load().catch((e) => setError(e.message ?? String(e)));
  }, [load]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMyId(data.user?.id ?? null));
  }, []);

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyKey(null);
    }
  }

  async function openPicker() {
    setPicking(true);
    if (!people) {
      try {
        setPeople(await listPeople(workspaceId));
      } catch (e: any) {
        setError(e?.message ?? String(e));
      }
    }
  }

  if (!roster) {
    return (
      <div className="card">
        <h2>Notifications for {brandName}</h2>
        <Loading label="Loading who's notified…" />
      </div>
    );
  }

  const memberIds = new Set(roster.members.map((m) => m.user_id));
  const me = roster.members.find((m) => m.user_id === myId) ?? null;
  const candidates = (people ?? []).filter((p) => !memberIds.has(p.user_id));
  const notifiedCount =
    roster.workspace_admins.length
    + roster.members.filter((m) => m.notifications_enabled
      && !roster.workspace_admins.some((a) => a.user_id === m.user_id)).length;

  return (
    <div className="card">
      <h2>Notifications for {brandName}</h2>
      <p className="muted">
        Who hears about it when this brand publishes. Being on this list grants nothing else —
        who may publish to {brandName}&rsquo;s accounts is set on each account.
      </p>

      {error && <p className="notice err">{error}</p>}

      {myId && (
        <p className="notice info">
          {me ? (
            <>
              You get notified when {brandName} publishes.{" "}
              <button
                type="button"
                className={busyKey === "me" ? "link is-busy" : "link"}
                disabled={busy}
                onClick={() => void run("me", () => setBrandNotifications(brandId, !me.notifications_enabled))}
              >
                {busyKey === "me" && <Spinner />}
                {me.notifications_enabled ? "Turn off for me" : "Turn back on for me"}
              </button>
            </>
          ) : (
            <>You are not on {brandName}&rsquo;s notification list.</>
          )}
        </p>
      )}

      <p className="muted">
        {notifiedCount} {notifiedCount === 1 ? "person" : "people"} will be notified next time{" "}
        {brandName} publishes.
      </p>

      <ul className="brand-list">
        {roster.workspace_admins.map((a) => (
          <li key={`admin:${a.user_id}`} className="brand-row">
            <span>{a.email ?? a.user_id}</span>
            <span className="muted">Workspace {a.role} — always notified</span>
          </li>
        ))}
        {roster.members.filter((m) => !roster.workspace_admins.some((a) => a.user_id === m.user_id))
          .map((m) => (
            <li key={`member:${m.user_id}`} className="brand-row">
              <span>{m.email ?? m.user_id}</span>
              <span className="muted">{m.notifications_enabled ? "Notified" : "Muted"}</span>
              <span className="spacer" />
              {(mayOrganise || m.user_id === myId) && (
                <button
                  type="button"
                  className={busyKey === `remove:${m.user_id}` ? "sm danger is-busy" : "sm danger"}
                  disabled={busy}
                  onClick={() => void run(`remove:${m.user_id}`, () => removeBrandMember(brandId, m.user_id))}
                >
                  {busyKey === `remove:${m.user_id}` && <Spinner />}
                  {m.user_id === myId ? "Leave" : "Remove"}
                </button>
              )}
            </li>
          ))}
        {roster.members.length === 0 && (
          <li className="brand-row muted">No one has been added to this brand's list yet.</li>
        )}
      </ul>

      {mayOrganise && (
        picking ? (
          <div className="row">
            <select value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy || !people}>
              <option value="">{people ? "Choose someone…" : "Loading people…"}</option>
              {candidates.map((p) => (
                <option key={p.user_id} value={p.user_id}>{p.email ?? p.user_id}</option>
              ))}
            </select>
            <button
              type="button"
              className={busyKey === "add" ? "primary is-busy" : "primary"}
              disabled={busy || !pick}
              onClick={() => void run("add", async () => {
                await addBrandMember(brandId, pick);
                setPick("");
                setPicking(false);
              })}
            >
              {busyKey === "add" && <Spinner />}
              {busyKey === "add" ? "Adding…" : "Add"}
            </button>
            <button type="button" className="sm" disabled={busy} onClick={() => setPicking(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <button type="button" className="sm" disabled={busy} onClick={() => void openPicker()}>
            Add someone to this brand's list
          </button>
        )
      )}
    </div>
  );
}
